import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { MapPin, Clock, Truck, NavigationArrow, XCircle, Timer, ArrowsLeftRight, ArrowLeft } from '@phosphor-icons/react';
import TripRouteMap from '../../components/TripRouteMap';
import BookingLiveTracker from './BookingLiveTracker';
import SortableExtraStops from '../../components/SortableExtraStops';
import RoutePreviewModal from '../../components/RoutePreviewModal';
import SupplierSearchSelect from '../../components/SupplierSearchSelect';

function TripDetailInline({ tripId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/trips/detail/${tripId}`).then(r => setData(r.data)).catch(() => {});
  }, [tripId]);
  if (!data) return <div className="text-xs text-[#666] py-2">Loading...</div>;
  const { trip, google_kms, manual_kms } = data;
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="border-t border-[#E5E5E5] pt-3 space-y-3">
      {trip.status === 'completed' && (
        <TripRouteMap tripId={tripId} height="220px" />
      )}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-[#F7F7F7] p-2 text-center">
          <p className="text-sm font-bold text-[#002FA7]">{google_kms || '-'}</p>
          <p className="text-[9px] uppercase tracking-widest text-[#666]">Google KMs</p>
        </div>
        <div className="bg-[#F7F7F7] p-2 text-center">
          <p className="text-sm font-bold text-[#00C853]">{manual_kms || '-'}</p>
          <p className="text-[9px] uppercase tracking-widest text-[#666]">Manual KMs</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {trip.started_at && <><span className="text-[#666]">Started</span><span>{new Date(trip.started_at).toLocaleTimeString()}</span></>}
        {trip.pickup_reached_at && <><span className="text-[#666]">Pickup Reached</span><span>{new Date(trip.pickup_reached_at).toLocaleTimeString()}</span></>}
        {trip.pickup_done_at && <><span className="text-[#666]">Pickup Done</span><span>{new Date(trip.pickup_done_at).toLocaleTimeString()}</span></>}
        {trip.completed_at && <><span className="text-[#666]">Completed</span><span>{new Date(trip.completed_at).toLocaleTimeString()}</span></>}
        {trip.pickup_waiting_minutes > 0 && <><span className="text-[#666]">Pickup Wait</span><span>{trip.pickup_waiting_minutes} min</span></>}
        {trip.drop_waiting_minutes > 0 && <><span className="text-[#666]">Drop Wait</span><span>{trip.drop_waiting_minutes} min</span></>}
        {trip.start_km > 0 && <><span className="text-[#666]">Start KM</span><span>{trip.start_km}</span></>}
        {trip.end_km > 0 && <><span className="text-[#666]">End KM</span><span>{trip.end_km}</span></>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {trip.start_km_photo && <img src={`${API_BASE}${trip.start_km_photo}`} alt="Start KM" className="w-16 h-16 object-cover border" />}
        {trip.goods_photo && <img src={`${API_BASE}${trip.goods_photo}`} alt="Goods" className="w-16 h-16 object-cover border" />}
        {trip.unload_photo && <img src={`${API_BASE}${trip.unload_photo}`} alt="Unload" className="w-16 h-16 object-cover border" />}
        {trip.receipt_photo && <img src={`${API_BASE}${trip.receipt_photo}`} alt="Receipt" className="w-16 h-16 object-cover border" />}
      </div>
      {trip.pickup_delay_reason && (
        <div className="bg-[#FF8C00]/10 p-2 text-xs text-[#FF8C00]">Delay: {trip.pickup_delay_reason.replace('_',' ')}</div>
      )}
    </div>
  );
}

const STATUS_BADGE = {
  awaiting_goods_ready: 'bg-[#A855F7]/10 text-[#A855F7]',
  pending: 'bg-[#808080]/10 text-[#808080]',
  allocated: 'bg-[#002FA7]/10 text-[#002FA7]',
  accepted: 'bg-[#00C853]/10 text-[#00C853]',
  in_progress: 'bg-[#002FA7]/10 text-[#002FA7]',
  pickup_reached: 'bg-[#FFD700]/10 text-[#B8860B]',
  pickup_done: 'bg-[#FF8C00]/10 text-[#FF8C00]',
  drop_reached: 'bg-[#FFD700]/10 text-[#B8860B]',
  completed: 'bg-[#00C853]/10 text-[#00C853]',
  cancelled: 'bg-[#FF3333]/10 text-[#FF3333]',
  pooled: 'bg-[#A855F7]/10 text-[#A855F7]',
};

const CANCELLABLE = ['awaiting_goods_ready', 'pending', 'allocated', 'accepted'];

// Phase 78y — 5-tab booking dashboard.
const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'allocated', label: 'Allocated' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
];

function fmtClockIST(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return '';
  }
}

export default function BookingTrips() {
  const [trips, setTrips] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Phase 78z — tab is driven by ?tab= so the BookingHome tiles can
  // deep-link directly to e.g. /booking/trips?tab=allocated.
  const urlTab = searchParams.get('tab');
  const allowedTabs = TABS.map(t => t.value);
  const initialTab = allowedTabs.includes(urlTab) ? urlTab : 'pending';
  const [tab, setTab] = useState(initialTab);
  const [liveTrackTripId, setLiveTrackTripId] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [detailTripId, setDetailTripId] = useState(null);
  // Phase 78ae — add-extra-stop dialog state
  const [stopDialog, setStopDialog] = useState(null); // { tripId, side: 'pickup'|'drop' }
  const [stopForm, setStopForm] = useState({ supplier_name: '', supplier_id: '', supplier_lat: 0, supplier_lng: 0, item: '', qty: 0, uom: '', remarks: '' });
  // Phase 78an — Masters for the Add Extra Stop dialog (suppliers /
  // items / uom). Fetched once and reused for every dialog open so we
  // don't hit the API per-click. Admin role also satisfies the
  // booking-staff gate.
  const [masters, setMasters] = useState({ suppliers: [], items: [], uoms: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, i, u] = await Promise.all([
          api.get('/booking/suppliers').catch(() => ({ data: [] })),
          api.get('/booking/items').catch(() => ({ data: [] })),
          api.get('/booking/uom').catch(() => ({ data: [] })),
        ]);
        if (!cancelled) setMasters({ suppliers: s.data || [], items: i.data || [], uoms: u.data || [] });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 78ad — keep state in sync if URL changes from another route
  // (e.g. user taps a different Home tile after coming back).
  useEffect(() => {
    if (urlTab && allowedTabs.includes(urlTab) && urlTab !== tab) {
      setTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  // Sync URL when tab changes programmatically.
  useEffect(() => {
    if (searchParams.get('tab') !== tab) {
      setSearchParams({ tab }, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  const fetchTrips = useCallback(async () => {
    try {
      const { data } = await api.get('/booking/trips', { params: { status: tab } });
      setTrips(data);
    } catch {}
  }, [tab]);

  useEffect(() => { fetchTrips(); const i = setInterval(fetchTrips, 15000); return () => clearInterval(i); }, [fetchTrips]);

  const handleCancel = async (tripId) => {
    try {
      await api.put(`/booking/trips/${tripId}/cancel`);
      fetchTrips();
      setCancelConfirm(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Cancel failed');
      setCancelConfirm(null);
    }
  };

  // Phase 78ag — Append OR insert an extra pickup or drop stop into an
  // existing trip. Allowed while the trip is anywhere from pending up
  // to drop_reached. Max 3 extras per side enforced by backend (1 main
  // + 3 extras = 4 total per side). `position` is the human-friendly
  // 1-indexed slot across ALL stops on the side (1 = main, 2/3/4 = the
  // extras slot the booker wants the new stop to occupy).
  const openStopDialog = (tripId, side) => {
    setStopDialog({ tripId, side });
    setStopForm({ supplier_name: '', supplier_id: '', supplier_lat: 0, supplier_lng: 0, item: '', qty: 0, uom: '', remarks: '', position: '' });
  };
  const submitExtraStop = async () => {
    if (!stopDialog) return;
    if (!stopForm.supplier_name.trim()) {
      alert('Supplier / location name is required');
      return;
    }
    try {
      const payload = {
        side: stopDialog.side,
        supplier_name: stopForm.supplier_name.trim(),
        location: stopForm.supplier_name.trim(),
        item: stopForm.item.trim(),
        qty: Number(stopForm.qty) || 0,
        uom: stopForm.uom.trim(),
        remarks: stopForm.remarks.trim(),
      };
      // Phase 78an — Pass lat/lng from the selected supplier so the
      // backend skips the master fallback round-trip and route preview
      // / chained ETA can use the coords immediately.
      if (stopForm.supplier_lat && stopForm.supplier_lng) {
        payload.lat = stopForm.supplier_lat;
        payload.lng = stopForm.supplier_lng;
      }
      // Position is optional; omit when blank so backend appends at end.
      if (stopForm.position) payload.position = Number(stopForm.position);
      await api.post(`/trips/${stopDialog.tripId}/extra-stop`, payload);
      setStopDialog(null);
      fetchTrips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Add stop failed');
    }
  };
  const deleteExtraStop = async (tripId, stopId) => {
    if (!window.confirm('Delete this extra stop?')) return;
    try {
      await api.delete(`/trips/${tripId}/extra-stop/${stopId}`);
      fetchTrips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Phase 78ai+aj — Ask Google Routes for the shortest order through
  // this trip's extras. By default we run a DRY-RUN first and surface
  // the suggested polyline + numbered stops in a modal so the booker
  // can visually approve before we mutate the trip.
  const [previewModal, setPreviewModal] = useState(null); // { tripId, preview }
  const optimizeStops = async (tripId) => {
    try {
      const { data } = await api.post(`/trips/${tripId}/extra-stops/optimize`, { dry_run: true });
      // If nothing changed on either side, fall through to a plain toast.
      const anyChange = data.pickup?.changed || data.drop?.changed;
      if (!anyChange) {
        const msg = [];
        for (const side of ['pickup', 'drop']) {
          const r = data[side];
          if (r?.reason) msg.push(`${side.toUpperCase()}: ${r.reason}`);
        }
        alert(msg.length ? msg.join('\n') : 'Nothing to optimise.');
        return;
      }
      setPreviewModal({ tripId, preview: data });
    } catch (err) {
      alert(err.response?.data?.detail || 'Optimisation failed');
    }
  };

  const canAddStops = (status) => [
    'awaiting_goods_ready', 'pending', 'allocated', 'accepted',
    'in_progress', 'pickup_reached', 'pickup_done', 'drop_reached',
  ].includes(status);

  return (
    <div data-testid="booking-trips" className="space-y-6">
      {liveTrackTripId && <BookingLiveTracker tripId={liveTrackTripId} onClose={() => setLiveTrackTripId(null)} />}

      {/* Phase 78ad — Dedicated single-bucket view.
          Header = Back button + the bucket label selected on the Home
          tile (e.g. "Pending", "Allocated"). The old 5-tab strip is
          gone; switching buckets is done by hitting Back and tapping
          another tile, which keeps each list focused and the mobile
          layout from cramping. */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/booking')}
          className="flex items-center gap-1 text-[#002FA7] hover:bg-[#002FA7]/5 px-2 py-1 rounded-full"
          data-testid="booking-trips-back"
          aria-label="Back to Booking Home"
        >
          <ArrowLeft size={18} weight="bold" />
          <span className="text-xs uppercase tracking-widest font-bold">Home</span>
        </button>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="booking-trips-title">
          {TABS.find(x => x.value === tab)?.label || 'Bookings'}
        </h1>
      </div>

      <div className="space-y-3">
        {trips.map(trip => (
          <div key={trip.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-3 space-y-2 shadow-sm" data-testid={`booking-trip-${trip.id}`}>
            <div className="flex items-start justify-between cursor-pointer" onClick={() => setDetailTripId(detailTripId === trip.id ? null : trip.id)}>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono text-xs text-[#666]">#{trip.id}</span>
                <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_BADGE[trip.status]}`}>
                  {trip.status === 'awaiting_goods_ready' ? 'Awaiting Goods' : trip.status === 'pooled' ? `Pooled with #${trip.is_pooled_into || '—'}` : trip.status}
                </span>
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${trip.priority === 'shortage' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#808080]/10 text-[#808080]'}`}>{trip.priority}</span>
                {trip.status === 'awaiting_goods_ready' && trip.material_ready_datetime && (
                  <span
                    className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-[#A855F7]/10 text-[#A855F7] font-bold"
                    data-testid={`trip-${trip.id}-awaiting`}
                    title="Auto-allocation will start at this time"
                  >
                    Ready @ {new Date(trip.material_ready_datetime).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                  </span>
                )}
                {trip.status === 'pending' && trip.queue_position && (
                  <span
                    className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-[#FF8C00]/10 text-[#FF8C00] font-bold"
                    data-testid={`trip-${trip.id}-queue`}
                  >
                    Queue #{trip.queue_position}
                  </span>
                )}
                {trip.allocation_type === 'auto_bunched' && (
                  <span
                    className="text-[10px] rounded-full px-2 py-0.5 bg-[#A855F7]/10 text-[#A855F7] font-bold flex items-center gap-1"
                    title={`This trip joined an existing pickup at ${trip.pickup_supplier_name || 'the same supplier'} — auto-bunched with another booking already heading there.`}
                    data-testid={`trip-${trip.id}-bunched`}
                  >
                    ✚ Bunched
                  </span>
                )}
              </div>
              <span className="text-[10px] text-[#666] whitespace-nowrap">{new Date(trip.created_at).toLocaleDateString()}</span>
            </div>

            <div className="flex items-center gap-2.5">
              <div className="flex flex-col items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-[#00C853]" />
                <div className="w-px h-6 bg-[#E5E5E5]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF3333]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {((trip.extra_pickups || []).length > 0) && (
                    <span className="font-mono text-[9px] text-[#00C853] mr-1.5 align-middle">PICKUP 1</span>
                  )}
                  {trip.pickup_supplier_name}
                </p>
                <p className="text-sm font-medium mt-2 truncate">
                  {((trip.extra_drops || []).length > 0) && (
                    <span className="font-mono text-[9px] text-[#FF3333] mr-1.5 align-middle">DROP 1</span>
                  )}
                  {trip.drop_supplier_name}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#666]">
              {trip.pilot_name && <span className="flex items-center gap-1"><Truck size={12} /> {trip.pilot_name}</span>}
              {trip.vehicle_number && <span className="font-mono">{trip.vehicle_number}</span>}
              {trip.vehicle_type_1 && <span className="bg-[#F3F3F3] rounded-full px-2 py-0.5">{trip.vehicle_type_1}</span>}
              {trip.item && <span>{trip.item} · {trip.qty} {trip.uom}</span>}
            </div>

            {/* Phase 78at — Surface Google-derived lifecycle timestamps so
                booking staff can verify when the pilot actually reached
                each milestone (vs. the pilot's tap-time which is shown
                separately). Shown only when at least one google_* time
                is populated to avoid clutter on pending trips. */}
            {(trip.google_start_time || trip.google_pickup_reach_time || trip.google_pickup_complete_time || trip.google_drop_reach_time || trip.google_drop_complete_time) && (
              <div className="rounded-xl border border-[#002FA7]/20 bg-[#002FA7]/5 px-3 py-2 space-y-1" data-testid={`booking-trip-${trip.id}-google-times`}>
                <p className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">Google GPS Times (auto-detected)</p>
                <div className="grid grid-cols-5 gap-1 text-[10px] text-[#0A0A0A]">
                  {[
                    ['Start',      trip.google_start_time],
                    ['PU Reach',   trip.google_pickup_reach_time],
                    ['PU Done',    trip.google_pickup_complete_time],
                    ['DR Reach',   trip.google_drop_reach_time],
                    ['DR Done',    trip.google_drop_complete_time],
                  ].map(([label, t]) => (
                    <div key={label} className="text-center">
                      <p className="text-[9px] uppercase tracking-widest text-[#666]">{label}</p>
                      <p className="font-mono text-[10px] font-bold">{t ? fmtClockIST(t) : '—'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phase 78y — Expected pickup reach time on Allocated tab. */}
            {tab === 'allocated' && trip.expected_pickup_reach_at && (
              <div
                className="rounded-xl border border-[#002FA7]/20 bg-[#002FA7]/5 px-3 py-2 flex items-center justify-between gap-2"
                data-testid={`booking-trip-${trip.id}-eta`}
                title={
                  trip.expected_pickup_source === 'google'
                    ? 'Google Routes (with live traffic) + allowed pickup waiting time'
                    : trip.expected_pickup_source === 'haversine'
                      ? 'Straight-line distance / 40 km/h + allowed pickup waiting time (Google Routes unavailable)'
                      : 'Pilot has not pinged GPS yet. Only allowed pickup waiting time counted.'
                }
              >
                <div className="flex items-center gap-2">
                  <Timer size={14} weight="bold" className="text-[#002FA7]" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold">Expected Pickup Reach</p>
                    <p className="text-sm font-bold text-[#002FA7]">{fmtClockIST(trip.expected_pickup_reach_at)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-[#666]">in</p>
                  <p className="text-xs font-bold text-[#0A0A0A]">
                    {trip.expected_pickup_reach_minutes ?? '—'}m
                  </p>
                  {(trip.expected_pickup_travel_minutes != null || trip.expected_pickup_wait_minutes != null) && (
                    <p className="text-[9px] text-[#666]">
                      <span className="font-mono">{trip.expected_pickup_travel_minutes ?? 0}m</span> drive
                      {' + '}
                      <span className="font-mono">{trip.expected_pickup_wait_minutes ?? 0}m</span> wait
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Phase 78y — Cancelled tab: surface who cancelled. */}
            {tab === 'cancelled' && (
              <div
                className="rounded-xl border border-[#FF3333]/20 bg-[#FF3333]/5 px-3 py-2 flex items-center justify-between gap-2 text-xs"
                data-testid={`booking-trip-${trip.id}-cancelled-by`}
              >
                <div className="flex items-center gap-2">
                  <XCircle size={14} className="text-[#FF3333]" />
                  <span className="text-[#666]">Cancelled by</span>
                  <span className="font-bold text-[#0A0A0A]">{trip.cancelled_by || '—'}</span>
                  {trip.cancelled_by_role && (
                    <span className="text-[10px] uppercase tracking-widest text-[#FF3333] bg-white rounded-full px-2 py-0.5 border border-[#FF3333]/20">
                      {trip.cancelled_by_role}
                    </span>
                  )}
                </div>
                {trip.cancelled_at && (
                  <span className="text-[10px] text-[#666] whitespace-nowrap">
                    {new Date(trip.cancelled_at).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                      timeZone: 'Asia/Kolkata',
                    })}
                  </span>
                )}
              </div>
            )}

            {/* Phase 78ae+ag+ah+ai — Extra pickup/drop stops with drag-to-
                reorder + Google-Routes "Suggest optimal order". Locked
                rows = stops the pilot has already reached (cannot be
                moved). */}
            {(['extra_pickups', 'extra_drops'].some(k => Array.isArray(trip[k]) && trip[k].length > 0)) && (
              <div className="rounded-xl border border-[#A855F7]/30 bg-[#F3E8FF] p-2 space-y-2" data-testid={`booking-trip-${trip.id}-extras`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-widest text-[#7C3AED] font-bold">
                    Additional Stops · drag rows to re-sequence
                  </p>
                  {canAddStops(trip.status) && (((trip.extra_pickups || []).length >= 2) || ((trip.extra_drops || []).length >= 2)) && (
                    <button
                      type="button"
                      onClick={() => optimizeStops(trip.id)}
                      className="text-[10px] uppercase tracking-widest font-bold bg-white text-[#7C3AED] border border-[#7C3AED]/40 rounded-full px-2.5 py-1 hover:bg-[#7C3AED] hover:text-white transition-colors"
                      data-testid={`optimize-stops-${trip.id}`}
                      title="Use Google Maps to suggest the shortest order"
                    >🧭 Optimise Order</button>
                  )}
                </div>
                <SortableExtraStops
                  tripId={trip.id}
                  side="pickup"
                  stops={trip.extra_pickups || []}
                  canEdit={canAddStops(trip.status)}
                  labelPrefix="PICKUP STOP"
                  colorHex="#00C853"
                  onChange={fetchTrips}
                  onDelete={(sid) => deleteExtraStop(trip.id, sid)}
                />
                <SortableExtraStops
                  tripId={trip.id}
                  side="drop"
                  stops={trip.extra_drops || []}
                  canEdit={canAddStops(trip.status)}
                  labelPrefix="DROP STOP"
                  colorHex="#FF3333"
                  onChange={fetchTrips}
                  onDelete={(sid) => deleteExtraStop(trip.id, sid)}
                />
              </div>
            )}

            {canAddStops(trip.status) && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => openStopDialog(trip.id, 'pickup')}
                  className="flex-1 text-[10px] uppercase tracking-widest text-[#00C853] border border-[#00C853]/30 rounded-full py-1.5 hover:bg-[#00C853]/5"
                  data-testid={`add-pickup-stop-${trip.id}`}
                  disabled={(trip.extra_pickups || []).length >= 3}
                  title={(trip.extra_pickups || []).length >= 3 ? 'Max 4 pickup points reached' : 'Add an extra pickup point'}
                >
                  + Add Pickup Stop ({1 + ((trip.extra_pickups || []).length)}/4)
                </button>
                <button
                  type="button"
                  onClick={() => openStopDialog(trip.id, 'drop')}
                  className="flex-1 text-[10px] uppercase tracking-widest text-[#FF3333] border border-[#FF3333]/30 rounded-full py-1.5 hover:bg-[#FF3333]/5"
                  data-testid={`add-drop-stop-${trip.id}`}
                  disabled={(trip.extra_drops || []).length >= 3}
                  title={(trip.extra_drops || []).length >= 3 ? 'Max 4 drop points reached' : 'Add an extra drop point'}
                >
                  + Add Drop Stop ({1 + ((trip.extra_drops || []).length)}/4)
                </button>
              </div>
            )}



            {/* Phase 78y — Pooled child trip details, surfaced everywhere a
                lead trip is shown. Each pooled booking carries its own
                pickup, drop, item, qty, remarks, booker info. */}
            {Array.isArray(trip.pooled_items) && trip.pooled_items.length > 0 && (
              <div
                className="rounded-xl border border-[#A855F7]/30 bg-[#A855F7]/5 p-2 space-y-2"
                data-testid={`booking-trip-${trip.id}-pooled`}
              >
                <div className="flex items-center gap-1.5">
                  <ArrowsLeftRight size={12} className="text-[#A855F7]" />
                  <span className="text-[10px] uppercase tracking-widest text-[#A855F7] font-bold">
                    Pooled Bookings ({trip.pooled_items.length})
                  </span>
                </div>
                {trip.pooled_items.map((pi, i) => (
                  <div
                    key={pi.id || i}
                    className="rounded-lg bg-white border border-[#A855F7]/15 px-2.5 py-2 text-[11px] space-y-1"
                    data-testid={`pooled-item-${trip.id}-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[#A855F7] font-bold">#{pi.booking_id || pi.id?.slice(0, 8) || `pool-${i + 1}`}</span>
                      {pi.priority && (
                        <span className="text-[9px] rounded-full px-1.5 py-0.5 bg-[#808080]/10 text-[#666]">{pi.priority}</span>
                      )}
                    </div>
                    {(pi.pickup_supplier_name || pi.drop_supplier_name) && (
                      <div className="flex items-start gap-1.5">
                        <div className="flex flex-col items-center pt-0.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00C853]" />
                          <div className="w-px h-3 bg-[#E5E5E5]" />
                          <div className="w-1.5 h-1.5 rounded-full bg-[#FF3333]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-[#0A0A0A]">{pi.pickup_supplier_name || '—'}</p>
                          <p className="truncate text-[#0A0A0A] mt-0.5">{pi.drop_supplier_name || '—'}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[#666]">
                      {pi.item && <span>{pi.item} · {pi.qty ?? 0} {pi.uom || ''}</span>}
                      {pi.booked_by_name && <span>By <span className="text-[#0A0A0A] font-medium">{pi.booked_by_name}</span></span>}
                      {pi.booked_from_halt && <span>From {pi.booked_from_halt}</span>}
                    </div>
                    {pi.remarks && (
                      <p className="text-[10px] text-[#B27300] bg-[#FFF8E6] border border-[#FFD27F]/40 rounded px-2 py-1">
                        <span className="font-bold mr-1">REMARK</span>{pi.remarks}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {trip.status === 'completed' && (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#F7F7F7] rounded-xl p-2 text-center">
                  <p className="text-sm font-bold text-[#002FA7]">{trip.google_kms || '-'}</p>
                  <p className="text-[9px] uppercase tracking-widest text-[#666]">Google KMs</p>
                </div>
                <div className="bg-[#F7F7F7] rounded-xl p-2 text-center">
                  <p className="text-sm font-bold text-[#00C853]">{trip.manual_kms || ((trip.end_km && trip.start_km) ? (trip.end_km - trip.start_km) : '-')}</p>
                  <p className="text-[9px] uppercase tracking-widest text-[#666]">Manual KMs</p>
                </div>
              </div>
            )}

            {trip.pilot_id && !['completed', 'cancelled', 'pending'].includes(trip.status) && (
              <button
                onClick={() => setLiveTrackTripId(trip.id)}
                className="w-full bg-[#002FA7] text-white py-2 flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-widest hover:bg-[#002FA7]/90 transition-colors rounded-full shadow-md"
                data-testid={`track-live-${trip.id}`}
              >
                <NavigationArrow size={14} weight="bold" className="animate-pulse-dot" /> Track Live
              </button>
            )}

            {CANCELLABLE.includes(trip.status) && (
              <button
                onClick={() => setCancelConfirm(trip)}
                className="w-full border border-[#FF3333]/30 text-[#FF3333] py-1.5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest hover:bg-[#FF3333]/5 transition-colors rounded-full"
                data-testid={`cancel-trip-${trip.id}`}
              >
                <XCircle size={12} /> Cancel Trip
              </button>
            )}

            {trip.pickup_waiting_minutes > 20 && (
              <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-xl p-2 text-[11px] text-[#FF3333]">
                <Clock size={12} className="inline mr-1" /> Excess pickup waiting: {trip.pickup_waiting_minutes} min
              </div>
            )}
            {trip.drop_waiting_minutes > 20 && (
              <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-xl p-2 text-[11px] text-[#FF3333]">
                <Clock size={12} className="inline mr-1" /> Excess drop waiting: {trip.drop_waiting_minutes} min
              </div>
            )}

            {detailTripId === trip.id && (
              <TripDetailInline tripId={trip.id} />
            )}
          </div>
        ))}
        {trips.length === 0 && (
          <div className="text-center py-12 text-[#666]" data-testid="booking-trips-empty">
            <MapPin size={48} className="mx-auto mb-4 text-[#E5E5E5]" />
            <p className="text-sm">No {TABS.find(t => t.value === tab)?.label?.toLowerCase() || tab} bookings</p>
          </div>
        )}
      </div>

      {/* Phase 78aj — Route preview modal (Google polyline + numbered
          markers + drive-time summary). Apply button calls optimize a
          second time with dry_run=false to actually persist the order. */}
      {previewModal && (
        <RoutePreviewModal
          tripId={previewModal.tripId}
          preview={previewModal.preview}
          onClose={() => setPreviewModal(null)}
          onApplied={() => { setPreviewModal(null); fetchTrips(); }}
        />
      )}

      {/* Phase 78ag — Add extra pickup/drop dialog with position selector.
          Order on the side = 1 (primary) + existing extras + this new
          stop. Booker picks which slot (2, 3, 4) the new stop will
          occupy; existing stops at that slot or below get pushed down. */}
      {stopDialog && (() => {
        const dlgTrip = trips.find(t => t.id === stopDialog.tripId) || {};
        const sideField = stopDialog.side === 'pickup' ? 'extra_pickups' : 'extra_drops';
        const existingCount = (dlgTrip[sideField] || []).length;
        // Valid slots: 2 (insert before first extra) … existingCount+2 (append at end)
        const slots = [];
        for (let p = 2; p <= existingCount + 2; p++) slots.push(p);
        return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md p-5 space-y-3 rounded-2xl" data-testid="extra-stop-dialog">
            <h2 className="text-lg font-bold">
              Add Extra {stopDialog.side === 'pickup' ? 'Pickup' : 'Drop'} Stop
            </h2>
            <p className="text-xs text-[#666]">Trip #{stopDialog.tripId?.slice(0, 8)} · 1 main + {existingCount} extras already</p>
            {/* Phase 78an — Supplier picker from master (same component
                as NewBooking so the search UX is identical). Selecting
                pulls lat/lng + address from the supplier doc. */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#666] block mb-1">
                Supplier *
              </label>
              <SupplierSearchSelect
                suppliers={masters.suppliers}
                value={stopForm.supplier_name}
                onSelect={(sup) => {
                  if (!sup) {
                    setStopForm({ ...stopForm, supplier_name: '', supplier_id: '', supplier_lat: 0, supplier_lng: 0 });
                  } else {
                    setStopForm({
                      ...stopForm,
                      supplier_name: sup.name,
                      supplier_id: sup.id,
                      supplier_lat: sup.lat || 0,
                      supplier_lng: sup.lng || 0,
                    });
                  }
                }}
                placeholder="Search & select from master"
                color={stopDialog.side === 'pickup' ? '#00C853' : '#FF3333'}
                required
                testIdPrefix="extra-stop-supplier"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#666] block mb-1">
                Insert at position (1 = main, 2-4 = extras)
              </label>
              <select
                value={stopForm.position}
                onChange={e => setStopForm({ ...stopForm, position: e.target.value })}
                className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm bg-white"
                data-testid="extra-stop-position"
              >
                <option value="">Append at end (position {existingCount + 2})</option>
                {slots.map(p => (
                  <option key={p} value={p}>Position {p}{p === existingCount + 2 ? ' (end)' : ` (push current ${p} down)`}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {/* Phase 78an — Item & UOM autocompletes backed by master. */}
              <div className="col-span-1">
                <label className="text-[9px] uppercase tracking-widest text-[#666] block mb-0.5">Item</label>
                <input
                  type="text" list={`extra-stop-item-list-${stopDialog.tripId}`}
                  placeholder="Item"
                  value={stopForm.item}
                  onChange={e => setStopForm({ ...stopForm, item: e.target.value })}
                  className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm"
                  data-testid="extra-stop-item"
                />
                <datalist id={`extra-stop-item-list-${stopDialog.tripId}`}>
                  {(masters.items || []).map(i => <option key={i.id} value={i.name} />)}
                </datalist>
              </div>
              <div className="col-span-1">
                <label className="text-[9px] uppercase tracking-widest text-[#666] block mb-0.5">Qty</label>
                <input
                  type="number"
                  placeholder="0"
                  value={stopForm.qty}
                  onChange={e => setStopForm({ ...stopForm, qty: e.target.value })}
                  className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm"
                  data-testid="extra-stop-qty"
                />
              </div>
              <div className="col-span-1">
                <label className="text-[9px] uppercase tracking-widest text-[#666] block mb-0.5">UOM</label>
                <input
                  type="text" list={`extra-stop-uom-list-${stopDialog.tripId}`}
                  placeholder="UOM"
                  value={stopForm.uom}
                  onChange={e => setStopForm({ ...stopForm, uom: e.target.value })}
                  className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm"
                  data-testid="extra-stop-uom"
                />
                <datalist id={`extra-stop-uom-list-${stopDialog.tripId}`}>
                  {(masters.uoms || []).map(u => <option key={u.id} value={u.name} />)}
                </datalist>
              </div>
            </div>
            <textarea
              placeholder="Remarks (optional)"
              value={stopForm.remarks}
              onChange={e => setStopForm({ ...stopForm, remarks: e.target.value })}
              className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-sm min-h-[60px]"
              data-testid="extra-stop-remarks"
            />
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => setStopDialog(null)}
                className="py-2 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] rounded-full"
                data-testid="extra-stop-cancel"
              >Cancel</button>
              <button
                onClick={submitExtraStop}
                className="py-2 text-sm font-bold uppercase tracking-widest bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-full"
                data-testid="extra-stop-save"
              >Add Stop</button>
            </div>
          </div>
        </div>
        );
      })()}


      {cancelConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm p-6 space-y-4 text-center rounded-2xl">
            <XCircle size={40} className="mx-auto text-[#FF3333]" />
            <p className="text-lg font-bold">Cancel this trip?</p>
            <p className="text-sm text-[#666]">
              #{cancelConfirm.id?.slice(0,8)} — {cancelConfirm.pickup_supplier_name} → {cancelConfirm.drop_supplier_name}
            </p>
            <p className="text-xs text-[#666]">Status: <span className="font-medium">{cancelConfirm.status}</span></p>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => setCancelConfirm(null)}
                className="py-3 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] rounded-full"
                data-testid="cancel-no-btn"
              >
                No
              </button>
              <button
                onClick={() => handleCancel(cancelConfirm.id)}
                className="py-3 text-sm font-bold uppercase tracking-widest bg-[#FF3333] text-white hover:bg-[#FF3333]/90 rounded-full"
                data-testid="cancel-yes-btn"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
